import { ReactElement } from "react";
import { connect, MapDispatchToProps, MapStateToProps, MergeProps } from "react-redux";

import {
    ControlReduxRawValue,
    ControlsKey,
    createSetValidationDescriptionsAction,
    createStableGetMergedValidationErrors,
    createValidateAction,
    ExplicitValidation,
    getRawValue,
    IExplicitValidationError,
    IExplicitValidationFnDescription,
    IInstantValidationFnDescription,
    ILocalizedExplicitValidationError,
    REQUIRED_INVALID_STATE_LOC_KEY,
} from "@multiweb/reactControls";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { IAppState } from "@multiweb/types/react";
import { OnValidationCallback, OnValidityChanged } from "../types";
import { getLocKeysToLoad } from "./utils";
import { ValidationErrorWrapperComponent, ValidationErrorWrapperComponentProps } from "./ValidationErrorWrapperComponent";

export type ValidationErrorWrapperChildrenProps = {
    invalid: boolean;
    /** Spustí validaci. */
    validate: (callback?: OnValidationCallback) => void;
};

export type ValidationErrorWrapperProps = {
    /** Unikátní identifikátor tooltipu. */
    tooltipId?: string;
    /** Identifikace **existující** controls instance v Reduxu. */
    dataKey: ControlsKey;
    /** Popisy určené k zaregistrovaní do Reduxu - k `dataKey`. */
    instantValidationFnDescriptions: ReadonlyArray<IInstantValidationFnDescription>;
    /** Popisy určené k zaregistrovaní do Reduxu - k `dataKey`. */
    explicitValidationFnDescriptions: ReadonlyArray<IExplicitValidationFnDescription>;
    /** Externí validační chyby. Tj. důvody nevalidnosti, které neřeší dané control. (Control je pouze dostane zvenku k zobrazení.) */
    externalErrors: ReadonlyArray<IExplicitValidationError>;
    /**
     * True, pokud se má v tooltipu zobrazit hláška, že hodnota je povinná, ale není vyplněna (platnou) hodnotou.
     * - V takovém případě se ignorují validační chyby (`errors` z Reduxu a `externalErrors`) - a v tooltipu se zobrazí pouze tato jediná hláška.
     * - Pozn. Toto není validační chyba! Proto musí být evidováno jako samostatný příznak. A nesmí to ovlivnit volání `onValidityChanged`.
     */
    isInRequiredInvalidState: boolean;
    /** Voláno pouze, pokud se výsledek validace změnil. */
    onValidityChanged: OnValidityChanged;
    /** Validované control. */
    children: (props: ValidationErrorWrapperChildrenProps) => ReactElement;
};

type StateProps = {
    validationErrors: ReadonlyArray<ILocalizedExplicitValidationError>;
    requiredInvalidError?: ILocalizedExplicitValidationError;
    rawValue: ControlReduxRawValue;
};

type DispatchProps = {
    /** Callback pro zaregistrování validací do reduxu. */
    onRegister: () => void;
    /**
     * Spouští validaci. Pouze se předá dál do `children`. `children` (např. `Edit`) si to pak mohou napojit např. na `onBlur`.
     * @param callback Callback je provolán vždy před spuštěním validace.
     */
    validate: (callback?: OnValidationCallback) => void;
};

const createMapStateToProps = (): MapStateToProps<StateProps, ValidationErrorWrapperProps, IAppState> => {
    const getMergedValidationErrors = createStableGetMergedValidationErrors();
    return (state, { dataKey, externalErrors, isInRequiredInvalidState }) => ({
        validationErrors: getMergedValidationErrors(state, { dataKey, externalErrors }),
        requiredInvalidError: (isInRequiredInvalidState ? ExplicitValidation.createLocalizedValidationError(getPrimaryText(state, REQUIRED_INVALID_STATE_LOC_KEY) ?? "") : undefined),
        rawValue: getRawValue(state, dataKey),
    });
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, ValidationErrorWrapperProps> = (
    dispatch,
    { dataKey, instantValidationFnDescriptions, explicitValidationFnDescriptions },
) => ({
    validate: (callback?: OnValidationCallback) => {
        callback && callback();
        dispatch(createValidateAction(dataKey));
    },
    onRegister: () => dispatch(createSetValidationDescriptionsAction(dataKey, instantValidationFnDescriptions, explicitValidationFnDescriptions)),
});

const mergeProps: MergeProps<StateProps, DispatchProps, ValidationErrorWrapperProps, ValidationErrorWrapperComponentProps> = (
    { validationErrors, requiredInvalidError },
    { onRegister, validate },
    {
        dataKey,
        tooltipId,
        instantValidationFnDescriptions,
        explicitValidationFnDescriptions,
        onValidityChanged,
        children,
    },
) => ({
    tooltipId,
    validationErrors,
    requiredInvalidError,
    registerDependencies: [dataKey, instantValidationFnDescriptions, explicitValidationFnDescriptions],
    locKeysToLoad: [...getLocKeysToLoad(explicitValidationFnDescriptions), REQUIRED_INVALID_STATE_LOC_KEY],
    onRegister,
    onValidityChanged,
    children: children({
        invalid: (validationErrors.length > 0 || !!requiredInvalidError),
        validate,
    }),
});

/**
 * Obálka pro validace controlu zajišťuje:
 * - Napojení na redux modulu `controls`, který uchovává stav validací.
 * - Zaregistrování validačních popisů.
 * - Funkce `validate` pro provedení (explicitní) validace nad daným control. Poskytnuta do control, které ji může napojit např. na `onBlur`.
 * - Tooltip s chybovými hláškami z validace - po najetí myši.
 */
export const ValidationErrorWrapper = connect(createMapStateToProps, mapDispatchToProps, mergeProps)(ValidationErrorWrapperComponent);
